import type { ActionState, OverrideWindow } from '../api/types'
import { BEHAVIOUR_TEXT, REJECT_TEXT, STATUS_TEXT, cancelledText, fmtRange } from '../lib/schedule'
import { ActionChip } from './ActionChip'

interface Props {
  windows: OverrideWindow[]
  maxOpen: number
  action: ActionState | null
  busyId: string | null
  onCancel: (w: OverrideWindow) => void
  cancelDisabled: boolean
}

function price(w: OverrideWindow): string {
  if (w.behaviour === 'FREE') return '0p/kWh'
  return w.price_p_per_kwh != null ? `${w.price_p_per_kwh}p/kWh` : ''
}

/** Upcoming-and-active list (counts toward the limit) and a collapsed 30-day history. */
export function WindowList({ windows, maxOpen, action, busyId, onCancel, cancelDisabled }: Props) {
  const open = windows.filter((w) => w.is_open)
  const past = windows.filter((w) => !w.is_open).sort((a, b) => b.window_start.localeCompare(a.window_start))

  return (
    <>
      <section className="card">
        <div className="card-heading">
          <h2>Upcoming and active</h2>
          <span className="muted">{open.length} of {maxOpen} used</span>
        </div>
        {open.length === 0 ? (
          <p className="muted">No windows set. Add one below.</p>
        ) : (
          <ul className="window-list">
            {open.map((w) => (
              <li key={w.request_id} className={`window-item window-${w.display_status.toLowerCase()}`}>
                <div className="window-main">
                  <span className="window-when">{fmtRange(w.window_start, w.window_end)}</span>
                  <span className="window-what">{BEHAVIOUR_TEXT[w.behaviour].label} <span className="muted">{price(w)}</span></span>
                </div>
                <div className="window-side">
                  {w.display_status === 'ACTIVE' || w.display_status === 'CANCELLING'
                    ? <ActionChip action={action} activeWindow={w} />
                    : <span className={`window-status status-pill-${w.display_status.toLowerCase()}`}>{STATUS_TEXT[w.display_status]}</span>}
                  {w.display_status !== 'CANCELLING' && (
                    <button
                      className="btn-secondary btn-small"
                      onClick={() => onCancel(w)}
                      disabled={cancelDisabled || busyId === w.request_id}
                    >
                      {busyId === w.request_id ? 'Cancelling…' : 'Cancel'}
                    </button>
                  )}
                </div>
                {w.display_status === 'PENDING' && (
                  <p className="window-note muted">Waiting for your system to confirm — usually within two minutes.</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {past.length > 0 && (
        <details className="card past-windows">
          <summary>Past 30 days ({past.length})</summary>
          <ul className="window-list">
            {past.map((w) => (
              <li key={w.request_id} className="window-item">
                <div className="window-main">
                  <span className="window-when">{fmtRange(w.window_start, w.window_end)}</span>
                  <span className="window-what">{BEHAVIOUR_TEXT[w.behaviour].label} <span className="muted">{price(w)}</span></span>
                </div>
                <div className="window-side">
                  <span className={`window-status status-pill-${w.display_status.toLowerCase()}`}>{STATUS_TEXT[w.display_status]}</span>
                </div>
                {w.display_status === 'REJECTED' && w.reject_reason && (
                  <p className="window-note muted">{REJECT_TEXT[w.reject_reason] ?? 'It could not be applied.'}</p>
                )}
                {w.display_status === 'CANCELLED' && <p className="window-note muted">{cancelledText(w)}</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  )
}
